/**
 * Les 15 épreuves jouables du Grand Quiz du Lair.
 * Chaque épreuve reçoit { data, onDone(points), cheat(lignes), sfx } : l'animateur clique pour le
 * candidat ; les réponses ne s'affichent que dans l'antisèche (touche R) ou la fenêtre régie.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { media, youTubeId } from './quizShowFormats';
import { KaraokeRound } from './QuizShowKaraoke';

const shuffleOnce = (arr, seed) => {
  const a = arr.map((x, i) => [x, i]);
  let s = seed || 1;
  for (let i = a.length - 1; i > 0; i -= 1) { s = (s * 9301 + 49297) % 233280; const j = Math.floor((s / 233280) * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
  return a;
};

export function Bar({ label, points, of, step, steps }) {
  return (
    <div className="qs-rbar">
      <span className="qs-rbar-label">{label}</span>
      {steps > 1 && <span className="qs-rbar-steps">{Array.from({ length: steps }, (_, i) => <i key={i} className={i < step ? 'is-done' : i === step ? 'is-now' : ''} />)}</span>}
      <span className="qs-rbar-pts"><b>{points}</b>{of ? <small> / {of}</small> : null} pts</span>
    </div>
  );
}

function Img({ src, className, alt = '', style }) {
  if (!src) return null;
  return <img className={className} src={media(src)} alt={alt} style={style} draggable="false" referrerPolicy="no-referrer" />;
}

function Tile({ item, n, className = '', onClick, children, disabled }) {
  return (
    <button type="button" className={`qs-tile ${className}`} onClick={onClick} disabled={disabled}>
      {n != null && <span className="qs-tile-n">{n}</span>}
      {item.image ? <Img src={item.image} className="qs-tile-img" /> : <span className="qs-tile-emoji">{item.label ? item.label.slice(0, 1) : '?'}</span>}
      <span className="qs-tile-label">{item.label}</span>
      {children}
    </button>
  );
}

function Timer({ seconds, running, onEnd, onTick }) {
  const [left, setLeft] = useState(seconds);
  const end = useRef(onEnd); end.current = onEnd;
  const tick = useRef(onTick); tick.current = onTick;
  useEffect(() => { setLeft(seconds); }, [seconds]);
  useEffect(() => {
    if (!running) return undefined;
    const t0 = Date.now(); const start = left;
    const id = setInterval(() => {
      const l = Math.max(0, start - Math.floor((Date.now() - t0) / 1000));
      setLeft((prev) => { if (prev !== l && l <= 5 && l > 0 && tick.current) tick.current(); return l; });
      if (l <= 0) { clearInterval(id); if (end.current) end.current(); }
    }, 200);
    return () => clearInterval(id);
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps
  const pct = (left / seconds) * 100;
  return (
    <div className={`qs-timer${left <= 10 ? ' is-low' : ''}`} style={{ '--p': `${pct}%` }}>
      <b>{left}</b><small>s</small>
    </div>
  );
}

function Done({ points, label = 'Terminer l’épreuve', onDone, note }) {
  return (
    <div className="qs-done">
      {note && <p>{note}</p>}
      <button type="button" className="qs-btn is-gold is-big" onClick={() => onDone(points)}>{label} · {points} pts</button>
    </div>
  );
}

// Contenu inutilisable : on l'explique plutôt que d'afficher un écran vide
export function Incomplete({ onDone, why }) {
  return (
    <div className="qs-round qs-incomplete">
      <h2 className="qs-q">Cette épreuve est incomplète</h2>
      <p className="qs-explain">{why} Complète-la dans l’éditeur, ou annule l’épreuve (en haut à droite) pour qu’elle reste jouable.</p>
      <Done points={0} onDone={onDone} label="Terminer quand même" />
    </div>
  );
}

// 1 · Quiz sous Haute Tension
const TENSION_PTS = [2, 4, 6, 7];
export function Tension({ data, onDone, cheat, sfx }) {
  const qs = data.questions || [];
  const [i, setI] = useState(0);
  const [pick, setPick] = useState(null);
  const [pts, setPts] = useState(0);
  const [over, setOver] = useState(false);
  const q = qs[i] || {};
  useEffect(() => { cheat([`Q${i + 1} : ${String.fromCharCode(65 + Number(q.answer || 0))}${(q.choices || [])[q.answer]?.text ? ` · ${q.choices[q.answer].text}` : ''}`]); }, [i]); // eslint-disable-line
  const choose = (k) => {
    if (pick != null) return;
    setPick(k);
    if (k === Number(q.answer)) { sfx('good'); setPts((p) => p + TENSION_PTS[i]); } else { sfx('bad'); setOver(true); }
  };
  const last = i >= qs.length - 1;
  return (
    <div className="qs-round qs-tension" style={{ '--volt': i / 3 }}>
      <Bar label={`Question ${i + 1} · ${TENSION_PTS[i]} pts`} points={pts} of={19} step={i} steps={4} />
      <div className="qs-volt">{Array.from({ length: 4 }, (_, k) => <i key={k} className={k <= i ? 'is-on' : ''} />)}</div>
      <h2 className="qs-q">{q.q}</h2>
      <Img src={q.image} className="qs-qimg" />
      <div className={`qs-choices n${(q.choices || []).length}${(q.choices || []).some((c) => c.image) ? ' has-img' : ''}`}>
        {(q.choices || []).map((c, k) => (
          <button type="button" key={k} className={`qs-choice${pick != null && k === Number(q.answer) ? ' is-good' : ''}${pick === k && k !== Number(q.answer) ? ' is-bad' : ''}`} onClick={() => choose(k)}>
            <span>{String.fromCharCode(65 + k)}</span>
            {c.image ? <Img src={c.image} className="qs-choice-img" /> : null}
            {c.text ? <em className="qs-choice-t">{c.text}</em> : null}
          </button>
        ))}
      </div>
      {pick != null && (over || last
        ? <Done points={pts} onDone={onDone} note={over ? 'Court-circuit ! Tu gardes tes points.' : 'Tu as tenu jusqu’au bout !'} />
        : <div className="qs-next"><button type="button" className="qs-btn is-big" onClick={() => { setI(i + 1); setPick(null); }}>Question suivante ⚡</button></div>)}
    </div>
  );
}

// 2 · Le Mur des Connexions
export function Mur({ data, onDone, cheat, sfx }) {
  const groups = (data.groups || []).filter((g) => (g.items || []).some((x) => x.label || x.image));
  const tiles = useMemo(() => shuffleOnce(groups.flatMap((g, gi) => (g.items || []).map((it) => ({ ...it, g: gi }))), groups.length * 31 + 5).map(([t]) => t), [data]); // eslint-disable-line
  const [found, setFound] = useState([]); // indices de groupes, dans l'ordre trouvé
  const [sel, setSel] = useState([]);
  const [errors, setErrors] = useState(0);
  const [links, setLinks] = useState({}); // groupe -> true/false
  const [askQ, setAskQ] = useState([]); // groupes dont on demande le lien
  const ask = askQ.length ? askQ[0] : null;
  const [shake, setShake] = useState(false);
  const frozen = errors >= 3;
  const left = groups.map((_, k) => k).filter((k) => !found.includes(k));
  const ended = (frozen || !left.length) && ask == null;
  const pts = found.length * 4 + Object.values(links).filter(Boolean).length;
  useEffect(() => { cheat(groups.map((g) => `${g.link || '?'} : ${(g.items || []).map((x) => x.label).join(', ')}`)); }, []); // eslint-disable-line
  const lock = (gi) => {
    const f = [...found, gi]; const q = [gi];
    const rest = groups.map((_, k) => k).filter((k) => !f.includes(k));
    if (rest.length === 1) { f.push(rest[0]); q.push(rest[0]); }
    setFound(f); setAskQ(q); setSel([]); sfx('good');
  };
  const judge = (ok) => { setLinks((l) => ({ ...l, [ask]: ok })); if (ok) sfx('win'); setAskQ((q) => q.slice(1)); };
  const click = (k) => {
    if (frozen || ask != null || found.includes(tiles[k].g)) return;
    const s2 = sel.includes(k) ? sel.filter((x) => x !== k) : [...sel, k];
    if (s2.length < 4) { setSel(s2); sfx('flip'); return; }
    const g0 = tiles[s2[0]].g;
    if (s2.every((x) => tiles[x].g === g0)) lock(g0);
    else { sfx('bad'); setSel(s2); setShake(true); setErrors((e) => e + 1); setTimeout(() => { setShake(false); setSel([]); }, 600); }
  };
  // Ordre d'affichage : groupes trouvés en haut, puis le reste
  const order = [...found.flatMap((gi) => tiles.map((t, k) => [t, k]).filter(([t]) => t.g === gi)), ...tiles.map((t, k) => [t, k]).filter(([t]) => !found.includes(t.g))];
  return (
    <div className="qs-round qs-mur">
      <Bar label={frozen ? 'Le mur est figé' : `${found.length} / ${groups.length} groupes`} points={pts} of={20} step={found.length} steps={groups.length} />
      <div className={`qs-wall${shake ? ' is-shake' : ''}${frozen ? ' is-frozen' : ''}`}>
        {order.map(([t, k]) => {
          const gi = found.indexOf(t.g);
          const showFrozen = frozen && gi < 0;
          return (
            <button type="button" key={k} onClick={() => click(k)} className={`qs-brick${sel.includes(k) ? ' is-sel' : ''}${gi >= 0 ? ` is-g g${gi}` : ''}${showFrozen ? ` is-g is-lost g${4 + (t.g % 4)}` : ''}`}>
              {t.image ? <Img src={t.image} className="qs-brick-img" /> : null}<span>{t.label}</span>
            </button>
          );
        })}
      </div>
      <div className="qs-strikes">{[0, 1, 2].map((k) => <i key={k} className={k < errors ? 'is-on' : ''}>✕</i>)}</div>
      {ask != null ? (
        <div className="qs-host qs-mur-ask">
          <span className="qs-host-tip">Le lien du groupe ?</span>
          <span className={`qs-mur-chip g${found.indexOf(ask)}`}>{(groups[ask].items || []).map((x) => x.label).join(' · ')}</span>
          <button type="button" className="qs-btn is-green" onClick={() => judge(true)}>✓ Lien trouvé (+1)</button>
          <button type="button" className="qs-btn is-ghost" onClick={() => judge(false)}>✕ Pas le lien</button>
        </div>
      ) : ended ? (
        <>
          <div className="qs-mur-links">{groups.map((g, k) => <span key={k} className={`g${found.indexOf(k) >= 0 ? found.indexOf(k) : 4 + k}`}>{g.link}{links[k] ? ' ✓' : ''}</span>)}</div>
          <Done points={pts} onDone={onDone} note={frozen ? 'Trois erreurs : le mur se fige.' : null} />
        </>
      ) : <div className="qs-host"><span className="qs-host-tip">Clique sur 4 cases qui vont ensemble</span></div>}
    </div>
  );
}

// 3 · Coordonnées Perdues
const coordPts = (d) => (d <= 0.03 ? 7 : d <= 0.06 ? 5 : d <= 0.1 ? 3 : d <= 0.15 ? 1 : 0);
export function Coords({ data, onDone, cheat, sfx }) {
  // Un lieu peut avoir une capture, ou seulement un nom (« Où se trouve… ? »), mais il lui faut une position
  const places = (data.places || []).filter((p) => (p.image || p.label) && p.pos);
  const [i, setI] = useState(0);
  const [pin, setPin] = useState(null);
  const [res, setRes] = useState(null);
  const [total, setTotal] = useState(0);
  const [ratio, setRatio] = useState(1);
  const p = places[i] || {};
  useEffect(() => { cheat([`Lieu ${i + 1} : ${p.label || '?'}`]); }, [i]); // eslint-disable-line
  const place = (e) => {
    if (res) return;
    const r = e.currentTarget.getBoundingClientRect();
    setRatio(r.height / r.width);
    setPin({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height });
    sfx('flip');
  };
  const validate = () => {
    if (!pin || !p.pos) return;
    const d = Math.hypot(pin.x - p.pos.x, (pin.y - p.pos.y) * ratio);
    const pts = coordPts(d);
    setRes({ d, pts }); setTotal((t) => t + pts); sfx(pts >= 5 ? 'good' : pts ? 'flip' : 'bad');
  };
  const last = i >= places.length - 1;
  if (!places.length || !data.map) return <Incomplete onDone={onDone} why={!data.map ? 'Il manque la carte du monde.' : 'Aucun lieu n’a de position sur la carte.'} />;
  return (
    <div className="qs-round qs-coords">
      <Bar label={`Lieu ${i + 1} / ${places.length}`} points={total} of={21} step={i} steps={places.length} />
      <div className="qs-coords-grid">
        <div className={`qs-coords-shot${p.image ? '' : ' is-name'}`}>{p.image ? <Img src={p.image} /> : <div className="qs-coords-ask"><small>Où se trouve…</small><b>{p.label}</b></div>}</div>
        <div className="qs-coords-map" onClick={place} role="presentation">
          <Img src={data.map} />
          {pin && <span className="qs-pin" style={{ left: `${pin.x * 100}%`, top: `${pin.y * 100}%` }} />}
          {res && p.pos && <span className="qs-pin is-true" style={{ left: `${p.pos.x * 100}%`, top: `${p.pos.y * 100}%` }} />}
          {res && p.pos && pin && (
            <svg className="qs-pin-line" viewBox="0 0 100 100" preserveAspectRatio="none"><line x1={pin.x * 100} y1={pin.y * 100} x2={p.pos.x * 100} y2={p.pos.y * 100} /></svg>
          )}
        </div>
      </div>
      {!res ? (
        <div className="qs-host">
          <span className="qs-host-tip">{pin ? 'Repère posé' : 'Clique sur la carte pour poser le repère'}</span>
          <button type="button" className="qs-btn is-gold" disabled={!pin} onClick={validate}>📍 Valider le repère</button>
        </div>
      ) : (
        <div className="qs-reveal">
          <h3>{p.label} · <b>{res.pts} pts</b></h3>
          <p>{res.pts === 7 ? 'Dans le mille !' : res.pts === 5 ? 'Tout près !' : res.pts === 3 ? 'Proche' : res.pts === 1 ? 'Dans le coin…' : 'Perdu dans la brume'}</p>
          {last ? <Done points={total} onDone={onDone} /> : <button type="button" className="qs-btn is-big" onClick={() => { setI(i + 1); setPin(null); setRes(null); }}>Lieu suivant 🧭</button>}
        </div>
      )}
    </div>
  );
}

// 4 · L'Exposition du Musée
const EXPO_TIERS = [[0, 'Salle vide'], [5, 'Quelques curieux'], [10, 'Bouche-à-oreille'], [15, 'Expo à succès'], [20, 'Chef-d’œuvre !']];
export function Expo({ data, onDone, cheat, sfx }) {
  const [phase, setPhase] = useState('intro');
  const [count, setCount] = useState(0);
  const pts = Math.min(20, count);
  useEffect(() => { cheat(String(data.answers || '').split('\n').filter(Boolean).slice(0, 60)); }, []); // eslint-disable-line
  useEffect(() => {
    if (phase !== 'run') return undefined;
    const k = (e) => { if (e.code === 'Space' && !/INPUT|TEXTAREA/.test(e.target.tagName)) { e.preventDefault(); setCount((c) => c + 1); sfx('good'); } };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [phase, sfx]);
  const tier = [...EXPO_TIERS].reverse().find(([n]) => pts >= n)[1];
  return (
    <div className="qs-round qs-expo">
      <Bar label="Réputation de l’expo" points={pts} of={20} />
      {phase === 'intro' && <div className="qs-center"><button type="button" className="qs-btn is-big is-gold" onClick={() => setPhase('read')}>Dévoiler le thème</button></div>}
      {phase !== 'intro' && <h2 className="qs-q qs-expo-topic">{data.topic}</h2>}
      {phase === 'read' && <div className="qs-center"><Timer seconds={5} running onEnd={() => { sfx('start'); setPhase('run'); }} /><p className="qs-muted">Prends connaissance du thème…</p></div>}
      {(phase === 'run' || phase === 'end') && (
        <>
          <div className="qs-expo-row">
            {phase === 'run' ? <Timer seconds={60} running onTick={() => sfx('tick')} onEnd={() => { sfx('buzz'); setPhase('end'); }} /> : <div className="qs-timer is-low"><b>0</b><small>s</small></div>}
            <div className="qs-gauge"><div className="qs-gauge-fill" style={{ height: `${(pts / 20) * 100}%` }} />{[5, 10, 15].map((n) => <i key={n} style={{ bottom: `${n * 5}%` }} />)}</div>
            <div className="qs-expo-count"><b>{count}</b><span>{tier}</span></div>
          </div>
          {phase === 'run' ? (
            <div className="qs-host">
              <span className="qs-host-tip">Espace = bonne réponse</span>
              <button type="button" className="qs-btn" onClick={() => setCount((c) => Math.max(0, c - 1))}>−1</button>
              <button type="button" className="qs-btn is-green is-big" onClick={() => { setCount((c) => c + 1); sfx('good'); }}>+1 bonne réponse</button>
              <button type="button" className="qs-btn is-ghost" onClick={() => setPhase('end')}>Stop</button>
            </div>
          ) : (
            <div className="qs-host"><button type="button" className="qs-btn" onClick={() => setCount((c) => Math.max(0, c - 1))}>−1 (correction)</button><button type="button" className="qs-btn" onClick={() => setCount((c) => c + 1)}>+1 (correction)</button><Done points={pts} onDone={onDone} /></div>
          )}
        </>
      )}
    </div>
  );
}

// 5 · Le Bon Match
export function Match({ data, onDone, cheat, sfx }) {
  const items = data.items || [];
  const links = (data.links || []).filter((l) => l.trait && Number(l.a) && Number(l.b));
  const [li, setLi] = useState(0);
  const [sel, setSel] = useState([]);
  const [matched, setMatched] = useState({});
  const [err, setErr] = useState(0);
  const [total, setTotal] = useState(0);
  const [shake, setShake] = useState(false);
  const link = links[li];
  const ended = li >= links.length;
  useEffect(() => { if (link) cheat([`${link.trait} : n°${link.a} ${items[link.a - 1]?.label || ''} + n°${link.b} ${items[link.b - 1]?.label || ''}`]); }, [li]); // eslint-disable-line
  const click = (k) => {
    if (ended || matched[k] != null) return;
    const s = sel.includes(k) ? sel.filter((x) => x !== k) : [...sel, k];
    if (s.length < 2) { setSel(s); sfx('flip'); return; }
    const pair = [Number(link.a) - 1, Number(link.b) - 1];
    if (s.every((x) => pair.includes(x))) {
      sfx('good');
      const g = Math.max(0, 4 - err);
      setTotal((t) => t + g); setMatched((m) => ({ ...m, [s[0]]: li, [s[1]]: li })); setSel([]); setErr(0); setLi(li + 1);
    } else {
      sfx('bad'); setErr((e) => e + 1); setShake(true); setTimeout(() => { setShake(false); setSel([]); }, 550);
      setSel(s);
    }
  };
  return (
    <div className="qs-round qs-match">
      <Bar label={ended ? 'Tous les matchs' : `Match ${li + 1} / ${links.length} · vaut ${Math.max(0, 4 - err)} pts`} points={total} of={20} step={li} steps={links.length} />
      {!ended && <h2 className="qs-q"><span className="qs-heart">💞</span> {link.trait}</h2>}
      <div className={`qs-grid25${shake ? ' is-shake' : ''}`}>
        {items.map((it, k) => (
          <Tile key={k} item={it} n={k + 1} onClick={() => click(k)} className={`${sel.includes(k) ? 'is-sel' : ''}${matched[k] != null ? ` is-matched m${matched[k]}` : ''}`} />
        ))}
      </div>
      {ended ? <Done points={total} onDone={onDone} /> : (
        <div className="qs-host"><button type="button" className="qs-btn is-ghost" onClick={() => { const pair = [Number(link.a) - 1, Number(link.b) - 1]; setMatched((m) => ({ ...m, [pair[0]]: -1, [pair[1]]: -1 })); setSel([]); setErr(0); setLi(li + 1); sfx('bad'); }}>Passer ce match (0 pt)</button></div>
      )}
    </div>
  );
}

// 6 · Lecture Mentale
export function QuiEstCe({ data, onDone, cheat, sfx }) {
  const items = data.items || [];
  const secret = Number(data.secret) - 1;
  const [off, setOff] = useState({});
  const [no, setNo] = useState(0);
  const [yes, setYes] = useState(0);
  const [final, setFinal] = useState(false);
  const [guess, setGuess] = useState(null);
  const pts = Math.max(0, 15 - no * 2);
  useEffect(() => { cheat([`Secret : n°${secret + 1} ${items[secret]?.label || '?'}`]); }, []); // eslint-disable-line
  const click = (k) => {
    if (guess != null) return;
    if (final) { setGuess(k); sfx(k === secret ? 'win' : 'bad'); return; }
    setOff((o) => ({ ...o, [k]: !o[k] })); sfx('flip');
  };
  const got = guess === secret;
  return (
    <div className="qs-round qs-mental">
      <Bar label={`${yes} oui · ${no} non`} points={guess != null ? (got ? pts : 0) : pts} of={15} />
      <h2 className="qs-q">{final ? (guess == null ? 'Réponse finale : quel est le personnage ?' : got ? 'Lecture réussie !' : `Raté… c’était ${items[secret]?.label}`) : 'Pose tes questions fermées'}</h2>
      <div className={`qs-grid25${final ? ' is-final' : ''}`}>
        {items.map((it, k) => (
          <Tile key={k} item={it} n={k + 1} onClick={() => click(k)} className={`${off[k] ? 'is-off' : ''}${guess != null && k === secret ? ' is-matched' : ''}${guess === k && !got ? ' is-wrong' : ''}`} />
        ))}
      </div>
      {guess != null ? <Done points={got ? pts : 0} onDone={onDone} /> : !final ? (
        <div className="qs-host">
          <span className="qs-host-tip">Clique sur une case pour l’éliminer</span>
          <button type="button" className="qs-btn is-green" onClick={() => { setYes(yes + 1); sfx('good'); }}>Oui</button>
          <button type="button" className="qs-btn is-red" onClick={() => { setNo(no + 1); sfx('bad'); }}>Non (−2)</button>
          <button type="button" className="qs-btn is-gold" onClick={() => setFinal(true)}>🔮 Réponse finale</button>
        </div>
      ) : <div className="qs-host"><span className="qs-host-tip">Clique sur le personnage choisi</span><button type="button" className="qs-btn is-ghost" onClick={() => setFinal(false)}>Retour</button></div>}
    </div>
  );
}

// Lecteur d'extrait : fichier audio ou YouTube (lecteur caché)
let ytReady = null;
const loadYT = () => {
  if (window.YT && window.YT.Player) return Promise.resolve();
  if (!ytReady) ytReady = new Promise((res) => { const prev = window.onYouTubeIframeAPIReady; window.onYouTubeIframeAPIReady = () => { if (prev) prev(); res(); }; const s = document.createElement('script'); s.src = 'https://www.youtube.com/iframe_api'; document.head.appendChild(s); });
  return ytReady;
};
function Clip({ src, start = 0, length = 10, label = 'Écouter l’extrait' }) {
  const [playing, setPlaying] = useState(false);
  const audio = useRef(null); const yt = useRef(null); const box = useRef(null); const stopT = useRef(0);
  const vid = youTubeId(src);
  useEffect(() => () => { clearTimeout(stopT.current); if (yt.current && yt.current.destroy) yt.current.destroy(); }, []);
  useEffect(() => { clearTimeout(stopT.current); setPlaying(false); if (yt.current && yt.current.destroy) { yt.current.destroy(); yt.current = null; } }, [src]);
  const stop = () => { clearTimeout(stopT.current); setPlaying(false); if (audio.current) audio.current.pause(); if (yt.current && yt.current.pauseVideo) yt.current.pauseVideo(); };
  const play = async () => {
    if (playing) { stop(); return; }
    setPlaying(true);
    const s = Number(start) || 0; const len = (Number(length) || 10) * 1000;
    if (vid) {
      await loadYT();
      if (!yt.current) {
        await new Promise((res) => { yt.current = new window.YT.Player(box.current, { width: 200, height: 200, videoId: vid, playerVars: { start: s, controls: 0, disablekb: 1, playsinline: 1 }, events: { onReady: res } }); });
      }
      yt.current.seekTo(s, true); yt.current.playVideo();
    } else if (audio.current) {
      audio.current.currentTime = s; audio.current.play().catch(() => setPlaying(false));
    }
    stopT.current = setTimeout(stop, len);
  };
  return (
    <div className="qs-clip">
      <button type="button" className={`qs-play${playing ? ' is-on' : ''}`} onClick={play}><span>{playing ? '■' : '▶'}</span>{playing ? 'Stop' : label}</button>
      {playing && <div className="qs-eq">{Array.from({ length: 12 }, (_, k) => <i key={k} style={{ animationDelay: `${-k * 0.13}s` }} />)}</div>}
      {!vid && src ? <audio ref={audio} src={media(src)} preload="auto" /> : null}
      <div className="qs-yt-hidden"><div ref={box} /></div>
    </div>
  );
}

// 7 · La Mixtape du DJ
export function Blind({ data, onDone, cheat, sfx }) {
  const tracks = (data.tracks || []).filter((t) => t.audio);
  const [i, setI] = useState(0);
  const [w, setW] = useState(false);
  const [d, setD] = useState(false);
  const [shown, setShown] = useState(false);
  const [total, setTotal] = useState(0);
  const t = tracks[i] || {};
  useEffect(() => { cheat([`Œuvre : ${t.work || '?'}`, `${t.detailQ || 'Détail'} ${t.detail || '?'}`]); }, [i]); // eslint-disable-line
  const reveal = () => { const g = (w ? 2 : 0) + (d ? 2 : 0); setTotal((x) => x + g); setShown(true); sfx(g ? 'good' : 'bad'); };
  const last = i >= tracks.length - 1;
  return (
    <div className="qs-round qs-blind">
      <Bar label={`Extrait ${i + 1} / ${tracks.length}`} points={total} of={16} step={i} steps={tracks.length} />
      <div className="qs-vinyl"><div className={`qs-disc${shown ? ' is-shown' : ''}`}>{shown && t.cover ? <Img src={t.cover} /> : <span>{i + 1}</span>}</div></div>
      <Clip src={t.audio} start={t.start} length={t.length} />
      <div className="qs-blind-asks">
        <div className={`qs-ask${shown ? (w ? ' is-good' : ' is-bad') : ''}`}><small>L’œuvre</small><b>{shown ? t.work : '?'}</b></div>
        <div className={`qs-ask${shown ? (d ? ' is-good' : ' is-bad') : ''}`}><small>{t.detailQ || 'Le détail'}</small><b>{shown ? t.detail : '?'}</b></div>
      </div>
      {!shown ? (
        <div className="qs-host">
          <button type="button" className={`qs-btn${w ? ' is-green' : ''}`} onClick={() => setW(!w)}>{w ? '✓' : '○'} Œuvre trouvée</button>
          <button type="button" className={`qs-btn${d ? ' is-green' : ''}`} onClick={() => setD(!d)}>{d ? '✓' : '○'} Détail trouvé</button>
          <button type="button" className="qs-btn is-gold" onClick={reveal}>Révéler</button>
        </div>
      ) : last ? <Done points={total} onDone={onDone} /> : <div className="qs-next"><button type="button" className="qs-btn is-big" onClick={() => { setI(i + 1); setW(false); setD(false); setShown(false); }}>Extrait suivant 🎧</button></div>}
    </div>
  );
}

// 8 · Carte Piège
export function Piege({ data, onDone, cheat, sfx }) {
  const cards = useMemo(() => shuffleOnce(data.cards || [], Math.floor(Math.random() * 100000) + 1), [data]);
  const [flip, setFlip] = useState({});
  const [ended, setEnded] = useState(false);
  const good = Object.keys(flip).filter((k) => !cards[k][0].trap).length;
  useEffect(() => { cheat(['Pièges : ' + cards.filter(([c]) => c.trap).map(([c]) => c.label).join(', ')]); }, []); // eslint-disable-line
  const turn = (k) => {
    if (ended || flip[k]) return;
    setFlip((f) => ({ ...f, [k]: true }));
    if (cards[k][0].trap) { sfx('trap'); setEnded(true); } else { sfx('good'); if (good + 1 >= cards.filter(([c]) => !c.trap).length) setEnded(true); }
  };
  return (
    <div className="qs-round qs-piege">
      <Bar label="Évite les pièges" points={good} of={20} />
      <h2 className="qs-q">{data.topic}</h2>
      <div className="qs-grid25">
        {cards.map(([c], k) => (
          <Tile key={k} item={c} onClick={() => turn(k)} className={`${flip[k] ? (c.trap ? 'is-wrong is-trap' : 'is-matched') : ''}${ended && !flip[k] && c.trap ? ' is-trapshow' : ''}`} />
        ))}
      </div>
      {ended ? <Done points={good} onDone={onDone} note={Object.keys(flip).some((k) => cards[k][0].trap) ? 'La carte était piégée !' : null} /> : (
        <div className="qs-host"><span className="qs-host-tip">Clique sur les cartes choisies</span><button type="button" className="qs-btn is-gold" onClick={() => { sfx('cash'); setEnded(true); }}>💰 S’arrêter avec {good} pts</button></div>
      )}
    </div>
  );
}

// 9 · Pixel Mystère : l'image est dessinée en gros pixels sur un canvas (sans lire ses pixels,
// donc sans souci de CORS), puis se précise palier par palier.
const PIX_LEVELS = [6, 10, 16, 26, 44]; // nombre de pixels sur la largeur, du plus flou au plus net
function PixelImg({ src, level }) {
  const box = useRef(null);
  const [img, setImg] = useState(null);
  useEffect(() => { setImg(null); if (!src) return undefined; const im = new Image(); im.referrerPolicy = 'no-referrer'; im.onload = () => setImg(im); im.src = media(src); return () => { im.onload = null; }; }, [src]);
  useEffect(() => {
    const cv = box.current; if (!cv || !img || level >= PIX_LEVELS.length) return;
    const W = cv.width; const H = cv.height;
    const r = Math.min(W / img.naturalWidth, H / img.naturalHeight);
    const w = img.naturalWidth * r; const h = img.naturalHeight * r;
    const cols = PIX_LEVELS[level]; const sw = Math.max(1, cols); const sh = Math.max(1, Math.round(cols * (h / w)));
    const tmp = document.createElement('canvas'); tmp.width = sw; tmp.height = sh;
    const t = tmp.getContext('2d'); t.imageSmoothingEnabled = true; t.drawImage(img, 0, 0, sw, sh);
    const c = cv.getContext('2d'); c.clearRect(0, 0, W, H); c.imageSmoothingEnabled = false;
    c.drawImage(tmp, 0, 0, sw, sh, (W - w) / 2, (H - h) / 2, w, h);
  }, [img, level]);
  if (level >= PIX_LEVELS.length) return <Img src={src} className="qs-pix-full" />;
  return <canvas ref={box} width={960} height={600} className="qs-pix-canvas" />;
}
export function VraiFaux({ data, onDone, cheat, sfx }) {
  const items = useMemo(() => (data.items || []).filter((x) => x.image), [data]);
  const [i, setI] = useState(0);
  const [lv, setLv] = useState(0);
  const [res, setRes] = useState(null);
  const [total, setTotal] = useState(0);
  const it = items[i] || {};
  const worth = PIX_LEVELS.length - lv;
  useEffect(() => { cheat([`Image ${i + 1} : ${it.answer || '?'}`]); }, [i]); // eslint-disable-line
  const next = () => { if (lv >= PIX_LEVELS.length - 1) { setLv(PIX_LEVELS.length); setRes({ ok: false, g: 0 }); sfx('bad'); } else { setLv(lv + 1); sfx('flip'); } };
  const found = () => { setRes({ ok: true, g: worth }); setTotal((t) => t + worth); setLv(PIX_LEVELS.length); sfx('win'); };
  const last = i >= items.length - 1;
  if (!items.length) return <Incomplete onDone={onDone} />;
  return (
    <div className="qs-round qs-pix">
      <Bar label={`Image ${i + 1} / ${items.length}${res ? '' : ` · palier ${lv + 1} / ${PIX_LEVELS.length} · vaut ${worth} pt${worth > 1 ? 's' : ''}`}`} points={total} of={items.length * PIX_LEVELS.length} step={i} steps={items.length} />
      <div className="qs-pix-screen"><PixelImg src={it.image} level={lv} /></div>
      <div className="qs-pix-steps">{PIX_LEVELS.map((_, k) => <i key={k} className={k < lv ? 'is-done' : k === lv && !res ? 'is-on' : ''}>{PIX_LEVELS.length - k}</i>)}</div>
      {!res ? (
        <div className="qs-host">
          <button type="button" className="qs-btn is-green" onClick={found}>✓ Trouvé ({worth} pt{worth > 1 ? 's' : ''})</button>
          <button type="button" className="qs-btn is-red" onClick={next}>{lv < PIX_LEVELS.length - 1 ? '✕ Raté : on précise' : '✕ Raté'}</button>
          {lv < PIX_LEVELS.length - 1 && <button type="button" className="qs-btn is-ghost" onClick={next}>Préciser l’image</button>}
        </div>
      ) : (
        <div className="qs-reveal"><h3>{it.answer} · <b>{res.g} pt{res.g > 1 ? 's' : ''}</b></h3>{last ? <Done points={total} onDone={onDone} /> : <button type="button" className="qs-btn is-big" onClick={() => { setI(i + 1); setLv(0); setRes(null); }}>Image suivante 👾</button>}</div>
      )}
    </div>
  );
}

// 10 · Les Fouilles Interdites
// Mur de 8 × 5 cases repérées comme une bataille navale (A1 … H5) : le candidat annonce la case à casser.
const DIG_COLS = 8; const DIG_ROWS = 5;
const COL_L = 'ABCDEFGH';
const digName = (k) => `${COL_L[k % DIG_COLS]}${Math.floor(k / DIG_COLS) + 1}`;
const DIG_MAX = 6;
// Une fois sur deux environ, l'image est zoomée et décentrée (parfois en miroir) :
// taper au milieu ne tombe plus forcément sur le personnage. Tout est remis droit à la révélation.
const digTwist = () => {
  const r = Math.random();
  const flip = Math.random() < 0.3 ? -1 : 1;
  if (r < 0.45) return { transform: `scaleX(${flip})` };
  const zoom = 1.5 + Math.random() * 0.8;
  const ox = Math.round(Math.random() * 100); const oy = Math.round(Math.random() * 100);
  return { transform: `scale(${(zoom * flip).toFixed(2)}, ${zoom.toFixed(2)})`, transformOrigin: `${ox}% ${oy}%` };
};
export function Fouilles({ data, onDone, cheat, sfx }) {
  const pieces = (data.pieces || []).filter((p) => p.image);
  const twists = useMemo(() => pieces.map(() => digTwist()), [data]); // eslint-disable-line
  const [i, setI] = useState(0);
  const [holes, setHoles] = useState([]);
  const [misses, setMisses] = useState(0);
  const [res, setRes] = useState(null);
  const [total, setTotal] = useState(0);
  const p = pieces[i] || {};
  const hits = holes.length;
  const used = hits + misses;
  const worth = used ? Math.max(1, 7 - used) : 6;
  useEffect(() => { cheat([`Image ${i + 1} : ${p.answer || '?'}`]); }, [i]); // eslint-disable-line
  const dig = (k) => { if (res || holes.includes(k) || hits >= DIG_MAX) return; sfx('dig'); setHoles((h) => [...h, k]); };
  const miss = () => { sfx('bad'); setMisses((m) => m + 1); };
  const finish = (ok) => { const g = ok ? worth : 0; setRes({ ok, g }); setTotal((t) => t + g); sfx(ok ? 'win' : 'bad'); };
  const last = i >= pieces.length - 1;
  return (
    <div className="qs-round qs-dig">
      <Bar label={`Image ${i + 1} / ${pieces.length} · ${res ? '' : `vaut ${worth} pts`}`} points={total} of={18} step={i} steps={pieces.length} />
      <div className="qs-dig-board">
        <span className="qs-dig-corner" />
        <div className="qs-dig-cols">{COL_L.split('').map((c) => <b key={c}>{c}</b>)}</div>
        <div className="qs-dig-rows">{Array.from({ length: DIG_ROWS }, (_, r) => <b key={r}>{r + 1}</b>)}</div>
        <div className={`qs-dig-wall${hits >= DIG_MAX && !res ? ' is-spent' : ''}`}>
          <Img src={p.image} style={res ? undefined : twists[i]} />
          <div className="qs-dig-grid" style={{ gridTemplateColumns: `repeat(${DIG_COLS}, 1fr)`, gridTemplateRows: `repeat(${DIG_ROWS}, 1fr)` }}>
            {Array.from({ length: DIG_COLS * DIG_ROWS }, (_, k) => (
              <button type="button" key={k} aria-label={`Case ${digName(k)}`} className={`qs-rock${holes.includes(k) || res ? ' is-broken' : ''}`} style={{ '--r': ((k * 37) % 7) / 7 }} onClick={() => dig(k)}><span>{digName(k)}</span></button>
            ))}
          </div>
        </div>
      </div>
      {!res ? (
        <div className="qs-host">
          <span className="qs-host-tip">{hits ? `Cases cassées (${hits}/${DIG_MAX}) : ${holes.map(digName).join(', ')}${hits >= DIG_MAX ? ' · plus de pioche, il faut répondre !' : ''}` : `Annonce une case (ex. « C3 ») · ${DIG_MAX} coups max`}{misses ? ` · ${misses} mauvaise${misses > 1 ? 's' : ''} réponse${misses > 1 ? 's' : ''}` : ''}</span>
          <button type="button" className="qs-btn is-green" disabled={!hits} onClick={() => finish(true)}>✓ Bonne réponse ({worth} pts)</button>
          <button type="button" className="qs-btn is-red" disabled={!hits} onClick={miss}>✗ Mauvaise réponse (−1 pt)</button>
          <button type="button" className="qs-btn is-ghost" onClick={() => finish(false)}>Abandonner</button>
        </div>
      ) : (
        <div className="qs-reveal"><h3>{p.answer} · <b>{res.g} pts</b></h3>{last ? <Done points={total} onDone={onDone} /> : <button type="button" className="qs-btn is-big" onClick={() => { setI(i + 1); setHoles([]); setMisses(0); setRes(null); }}>Image suivante ⛏️</button>}</div>
      )}
    </div>
  );
}

// 11 · Stats dans l'Ordre
export function Ordre({ data, onDone, cheat, sfx }) {
  const items = (data.items || []).filter((x) => x.label);
  const [placed, setPlaced] = useState(items.length ? [0] : []);
  const [next, setNext] = useState(1);
  const [total, setTotal] = useState(0);
  const [fail, setFail] = useState(null);
  const cur = items[next];
  const ended = fail != null || next >= items.length;
  useEffect(() => {
    if (!cur) return;
    const sorted = [...placed, next].sort((a, b) => Number(items[a].value) - Number(items[b].value)).map((k) => items[k].label);
    cheat([`${cur.label} : ${cur.display || cur.value}`, `Ordre : ${sorted.join(' < ')}`]);
  }, [next]); // eslint-disable-line
  const drop = (gap) => {
    if (ended) return;
    const v = Number(cur.value);
    const lo = gap > 0 ? Number(items[placed[gap - 1]].value) : -Infinity;
    const hi = gap < placed.length ? Number(items[placed[gap]].value) : Infinity;
    const np = [...placed]; np.splice(gap, 0, next);
    if (v >= lo && v <= hi) { sfx('good'); setTotal((t) => t + next); setPlaced(np); setNext(next + 1); } else {
      sfx('bad');
      const right = [...placed, next].sort((a, b) => Number(items[a].value) - Number(items[b].value));
      setFail({ gap }); setPlaced(right);
    }
  };
  const Card = ({ k, show }) => (
    <div className={`qs-ocard${fail && k === next ? ' is-bad' : ''}`}><Img src={items[k].image} /><b>{items[k].label}</b>{show && <small>{items[k].display || items[k].value}</small>}</div>
  );
  return (
    <div className="qs-round qs-ordre">
      <Bar label={ended ? data.criterion : `${data.criterion} · placement à ${next} pt${next > 1 ? 's' : ''}`} points={total} of={21} step={next - 1} steps={items.length - 1} />
      {!ended && cur && <div className="qs-ordre-cur"><span>À placer</span><Card k={next} /></div>}
      <div className="qs-ordre-scale">
        <span className="qs-ordre-end">{data.low}</span>
        {placed.map((k, g) => (
          <React.Fragment key={k}>
            {!ended && <button type="button" className="qs-gap" onClick={() => drop(g)}>＋</button>}
            <Card k={k} show={ended} />
          </React.Fragment>
        ))}
        {!ended && <button type="button" className="qs-gap" onClick={() => drop(placed.length)}>＋</button>}
        <span className="qs-ordre-end">{data.high}</span>
      </div>
      {ended && <Done points={total} onDone={onDone} note={fail ? 'L’IA est perplexe… placement raté.' : 'Classement parfait !'} />}
    </div>
  );
}

// 12 · L'Intrus de la Bande
export function Intrus({ data, onDone, cheat, sfx }) {
  const series = (data.series || []).filter((x) => (x.items || []).some((i) => i.label || i.image));
  const [i, setI] = useState(0);
  const [pick, setPick] = useState(null);
  const [why, setWhy] = useState(null);
  const [total, setTotal] = useState(0);
  const s0 = series[i] || { items: [] };
  const bad = Number(s0.intrus) - 1;
  useEffect(() => { cheat([`Intrus : n°${bad + 1} ${s0.items[bad]?.label || '?'}`, `Lien : ${s0.reason || '?'}`]); }, [i]); // eslint-disable-line
  const choose = (k) => { if (pick != null) return; setPick(k); if (k === bad) { sfx('good'); setTotal((t) => t + 3); } else { sfx('bad'); setWhy(false); } };
  const judge = (ok) => { setWhy(ok); if (ok) { setTotal((t) => t + 1); sfx('win'); } };
  const last = i >= series.length - 1;
  const settled = pick != null && why != null;
  return (
    <div className="qs-round qs-intrus">
      <Bar label={`Série ${i + 1} / ${series.length}`} points={total} of={series.length * 4} step={i} steps={series.length} />
      <h2 className="qs-q">🕵️ Qui n’a rien à faire là ?</h2>
      <div className="qs-suspects">
        {s0.items.map((it, k) => (
          <button type="button" key={k} onClick={() => choose(k)} className={`qs-suspect${pick != null && k === bad ? ' is-intrus' : ''}${pick === k && k !== bad ? ' is-wrong' : ''}${pick != null && k !== bad && pick !== k ? ' is-gang' : ''}`}>
            <span className="qs-suspect-n">{k + 1}</span>
            {it.image ? <Img src={it.image} className="qs-suspect-img" /> : <span className="qs-suspect-mask">?</span>}
            <b>{it.label}</b>
            {pick != null && k === bad && <em>INTRUS</em>}
          </button>
        ))}
      </div>
      {pick != null && why == null && (
        <div className="qs-host"><span className="qs-host-tip">Démasqué ! Et le lien entre les 4 autres ?</span><button type="button" className="qs-btn is-green" onClick={() => judge(true)}>✓ Bonne explication (+1)</button><button type="button" className="qs-btn is-ghost" onClick={() => judge(false)}>✕ Pas le bon lien</button></div>
      )}
      {settled && s0.reason && <div className={`qs-answer${pick === bad ? ' is-good' : ' is-bad'}`}>{s0.reason}</div>}
      {settled && (last ? <Done points={total} onDone={onDone} /> : <div className="qs-next"><button type="button" className="qs-btn is-big" onClick={() => { setI(i + 1); setPick(null); setWhy(null); }}>Série suivante 🕵️</button></div>)}
    </div>
  );
}

// 13 · Les Mots Manquants : karaoké à trous (voir QuizShowKaraoke)
export function Mots(props) {
  return <KaraokeRound {...props} Bar={Bar} Done={Done} />;
}

// 14 · Œil de Lynx
export function Lynx({ data, onDone, cheat, sfx }) {
  const qs = (data.questions || []).filter((q) => q.q);
  const [phase, setPhase] = useState('intro');
  const [i, setI] = useState(0);
  const [lvl, setLvl] = useState(1);
  const [res, setRes] = useState(null);
  const [total, setTotal] = useState(0);
  const q = qs[i] || {};
  useEffect(() => { if (phase === 'quiz') cheat([`Q${i + 1} : ${q.answer || '?'}`]); }, [i, phase]); // eslint-disable-line
  const judge = (ok) => {
    setRes(ok);
    if (ok) { setTotal((t) => t + lvl); setLvl(lvl + 1); sfx('fire'); } else { setLvl(Math.max(1, lvl - 1)); sfx('bad'); }
  };
  const last = i >= qs.length - 1;
  return (
    <div className="qs-round qs-lynx">
      <Bar label={phase === 'quiz' ? `Question ${i + 1} / ${qs.length} · flamme ${lvl}` : 'Observation'} points={total} of={21} step={phase === 'quiz' ? i : 0} steps={qs.length} />
      {phase === 'intro' && <div className="qs-center"><button type="button" className="qs-btn is-big is-gold" onClick={() => { sfx('start'); setPhase('look'); }}>Afficher l’image (60 s)</button></div>}
      {phase === 'look' && (
        <div className="qs-lynx-look">
          <Img src={data.image} />
          <div className="qs-lynx-bar"><Timer seconds={60} running onTick={() => sfx('tick')} onEnd={() => { sfx('buzz'); setPhase('quiz'); }} /><button type="button" className="qs-btn is-ghost" onClick={() => setPhase('quiz')}>Passer aux questions</button></div>
        </div>
      )}
      {phase === 'quiz' && (
        <>
          <div className="qs-flame" style={{ '--lvl': Math.min(lvl, 7) }}><span>🔥</span><b>{lvl} pt{lvl > 1 ? 's' : ''}</b></div>
          <h2 className="qs-q">{q.q}</h2>
          {res != null && <div className={`qs-answer${res ? ' is-good' : ' is-bad'}`}>{q.answer}</div>}
          {res == null ? (
            <div className="qs-host"><button type="button" className="qs-btn is-green" onClick={() => judge(true)}>✓ Bonne réponse</button><button type="button" className="qs-btn is-red" onClick={() => judge(false)}>✕ Mauvaise réponse</button></div>
          ) : last ? (
            <><div className="qs-lynx-mini"><Img src={data.image} /></div><Done points={total} onDone={onDone} /></>
          ) : <div className="qs-next"><button type="button" className="qs-btn is-big" onClick={() => { setI(i + 1); setRes(null); }}>Question suivante 🔥</button></div>}
        </>
      )}
    </div>
  );
}

// 15 · Le Labo des Énigmes : trois fioles d'indices, du plus obscur au plus évident (3, 2 puis 1 pt)
const fiolesOf = (r) => [r.clue, r.clue2 || r.hint, r.clue3].filter(Boolean);
export function Labo({ data, onDone, cheat, sfx }) {
  const rs = (data.riddles || []).filter((r) => r.answer && r.clue);
  const [i, setI] = useState(0);
  const [open, setOpen] = useState(1); // fioles ouvertes
  const [res, setRes] = useState(null);
  const [total, setTotal] = useState(0);
  const r = rs[i] || {};
  const fioles = fiolesOf(r);
  const worth = Math.max(1, 4 - open);
  useEffect(() => { cheat([`Réponse : ${r.answer || '?'}`, ...fiolesOf(r).slice(1).map((c, k) => `Fiole ${k + 2} : ${c}`)]); }, [i]); // eslint-disable-line
  const found = () => { setRes(true); setTotal((t) => t + worth); sfx('win'); };
  const wrong = () => { if (open < fioles.length) { setOpen(open + 1); sfx('bad'); } else { setRes(false); sfx('bad'); } };
  const last = i >= rs.length - 1;
  if (!rs.length) return <Incomplete onDone={onDone} why="Aucune énigme complète." />;
  return (
    <div className="qs-round qs-labo">
      <Bar label={`Expérience ${i + 1} / ${rs.length} · vaut ${res == null ? worth : res ? worth : 0} pt${worth > 1 ? 's' : ''}`} points={total} of={rs.length * 3} step={i} steps={rs.length} />
      <div className="qs-fioles">
        {fioles.map((c, k) => {
          const shown = k < open || res != null;
          const emojiOnly = !/[A-Za-zÀ-ÿ0-9]/.test(c);
          return (
            <div key={k} className={`qs-fiole${shown ? ' is-open' : ''}`} style={{ '--k': k }}>
              <div className="qs-fiole-head"><span>Fiole {k + 1}</span><b>{3 - k} pt{3 - k > 1 ? 's' : ''}</b></div>
              <div className="qs-fiole-body">{shown ? <p className={emojiOnly ? 'qs-emoji' : ''}>{c}</p> : <i>?</i>}</div>
            </div>
          );
        })}
      </div>
      {res != null && <div className="qs-labo-reveal">{r.image && <Img src={r.image} className="qs-qimg" />}<div className={`qs-answer${res ? ' is-good' : ' is-bad'}`}>{r.answer}</div></div>}
      {res == null ? (
        <div className="qs-host">
          <button type="button" className="qs-btn is-green" onClick={found}>✓ Trouvé ({worth} pt{worth > 1 ? 's' : ''})</button>
          <button type="button" className="qs-btn is-red" onClick={wrong}>{open < fioles.length ? '✕ Raté : fiole suivante' : '✕ Raté'}</button>
          {open < fioles.length && <button type="button" className="qs-btn is-ghost" onClick={() => { setOpen(open + 1); sfx('flip'); }}>Ouvrir la fiole suivante</button>}
        </div>
      ) : last ? <Done points={total} onDone={onDone} /> : <div className="qs-next"><button type="button" className="qs-btn is-big" onClick={() => { setI(i + 1); setOpen(1); setRes(null); }}>Expérience suivante 🧪</button></div>}
    </div>
  );
}

export const ROUNDS = { tension: Tension, mur: Mur, coords: Coords, expo: Expo, match: Match, quiestce: QuiEstCe, blind: Blind, piege: Piege, vraifaux: VraiFaux, fouilles: Fouilles, ordre: Ordre, intrus: Intrus, mots: Mots, lynx: Lynx, labo: Labo };
